import React from "react"
import { localize } from "locale"
import { P } from "styledComponents/Typography"
import { Box } from "styledComponents/Containers"
import Columns from "components/Columns"
import TitleBox from "./TitleBox"
import Subquestion from "./Subquestion"
import * as R from "ramda"
import { nameSubtypes, valueSubtypes } from "./subtypes"
import { getIsOpen } from "./helpers"
import useOpen from "hooks/useOpen"

const Question = ({
  node,
  onChange,
  elementId,
  elementOptionId,
  value,
  expand,
  scroller,
  lastSelectedValue,
}) => {
  const { isOpen, toggle } = useOpen(
    getIsOpen({ value, lastSelectedValue, node }),
  )

  const t = node.data.questionType
  const isChoice = t === "Single" || t === "Multiple" || t === "Confirmation"
  const isCustom = t === "Custom"
  const isName = t === "Name"
  const isValue = t === "Value"
  const isSpecial = isName || isValue
  const isOpenable = isChoice || isCustom || isSpecial
  const isExpanded = isOpen || expand

  const elIdObj = elementOptionId ? { elementOptionId } : { elementId }
  const mapIdObj = value && R.pick(["optionMappingId", "mappingId"], value)

  return (
    <Box mb={15}>
      <Columns widths={[130, "auto"]} mb={5}>
        <TitleBox
          toggle={toggle}
          onSelect={() =>
            onChange({
              questionId: node.referencedId,
              ...elIdObj,
              ...mapIdObj,
              text: "Q" + node.idx,
            })
          }
          isOpenable={isOpenable}
          isSelectable={!isOpenable || isName}
          isOpen={isExpanded}
          isActive={
            value && value.questionId === node.referencedId && !value.subType
          }
          wasActiveInLastSelection={
            lastSelectedValue &&
            lastSelectedValue.questionId === node.referencedId &&
            !lastSelectedValue.subType
          }
          title={`${localize("question")} ${node.idx}`}
          scroller={scroller}
        />
        <P mt={3}>{node.data.questionText}</P>
      </Columns>
      {isChoice && isExpanded && (
        <Box ml={60}>
          {node.data.options.map((x, xi) => (
            <Columns widths={[90, "auto"]} mb={10} key={xi}>
              <TitleBox
                onSelect={() =>
                  onChange({
                    questionOptionId: x.id,
                    ...elIdObj,
                    ...mapIdObj,
                    text: `Q${node.idx} / ${localize("answer")} ${xi + 1}`,
                  })
                }
                isSelectable
                noToggle
                isOpen={isExpanded}
                isActive={value && value.questionOptionId === x.id}
                wasActiveInLastSelection={
                  lastSelectedValue &&
                  lastSelectedValue.questionOptionId === x.id
                }
                title={`${localize("answer")} ${xi + 1}`}
                scroller={scroller}
              />
              <P mt={3}>{x.text}</P>
            </Columns>
          ))}
        </Box>
      )}
      {isCustom && isExpanded && (
        <Box ml={60}>
          {node.data.subQuestions.map((x, xi) => (
            <Subquestion
              key={xi}
              subquestion={x}
              indices={[node.idx, xi + 1]}
              onChange={onChange}
              elementId={elementId}
              elementOptionId={elementOptionId}
              elIdObj={elIdObj}
              mapIdObj={mapIdObj}
              node={node}
              value={value}
              expand={expand}
              scroller={scroller}
              lastSelectedValue={lastSelectedValue}
            />
          ))}
        </Box>
      )}
      {isSpecial && isExpanded && (
        <Box flex ml={60}>
          {(isName ? nameSubtypes : valueSubtypes).map(x => (
            <TitleBox
              key={x.subType}
              toggle={toggle}
              onSelect={() =>
                onChange({
                  questionId: node.referencedId,
                  ...elIdObj,
                  ...mapIdObj,
                  subType: x.subType,
                  text: `Q${node.idx} / ${x.name}`,
                })
              }
              isSelectable
              isActive={
                value &&
                value.questionId === node.referencedId &&
                value.subType === x.subType
              }
              wasActiveInLastSelection={
                lastSelectedValue &&
                lastSelectedValue.questionId === node.referencedId &&
                lastSelectedValue.subType === x.subType
              }
              title={x.name}
              scroller={scroller}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default Question
